import { Lock } from 'lucide-react';
import { useBranding } from '../../hooks/useBranding';

/**
 * "Not included in your plan", said the same way everywhere (Phase 7a).
 *
 * A module the plan does not include is shown, locked, rather than hidden:
 * an MSP that cannot see an add-on exists will never ask for it. The person
 * to ask is whoever provides the portal, which is the support contact the
 * portal already carries in its branding.
 */
export function notInPlanMessage(supportEmail) {
  return supportEmail ? `Not included in your plan — contact ${supportEmail}` : 'Not included in your plan — contact your provider';
}

/** The one-line form, for a tooltip or a note under a disabled control. */
export function useNotInPlanMessage() {
  const branding = useBranding();
  return notInPlanMessage(branding?.supportEmail);
}

/** A short inline notice beside a locked control. */
export function NotInPlanNotice({ className = '' }) {
  const message = useNotInPlanMessage();
  return (
    <span className={`inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300 ${className}`}>
      <Lock className="h-3 w-3" /> {message}
    </span>
  );
}

/** The full page, shown in place of a route the plan does not include. */
export default function NotInPlan({ feature = null }) {
  const message = useNotInPlanMessage();
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <Lock className="mx-auto mb-4 h-10 w-10 text-neutral-400" />
        <h1 className="mb-2 text-2xl font-bold text-neutral-900 dark:text-white">Not included in your plan</h1>
        <p className="mb-6 text-neutral-600 dark:text-neutral-300">
          {message}
          {feature ? <span className="mt-2 block font-mono text-xs text-neutral-400">{feature}</span> : null}
        </p>
        <a href="/dashboard" className="btn-primary">Go to Dashboard</a>
      </div>
    </div>
  );
}
